import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Loader2, Save, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { MissingVars } from "../SendForm";

export const inputCls = "w-full px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

function VariablePicker({ vars, onPick }) {
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const out = {};
    vars.filter((v) => !s || v.label.toLowerCase().includes(s) || v.key.includes(s)).forEach((v) => { (out[v.group] = out[v.group] || []).push(v); });
    return Object.entries(out);
  }, [vars, q]);
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="tpl-variables">
      <div className="relative mb-2">
        <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-norea-textMuted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher une variable…" className={`${inputCls} pl-7`} data-testid="tpl-variables-search" />
      </div>
      <div className="flex-1 overflow-y-auto pr-1 space-y-3">
        {groups.map(([g, list]) => (
          <div key={g}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-norea-textMuted mb-1">{g}</div>
            <div className="flex flex-wrap gap-1">
              {list.map((v) => (
                <button key={v.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(v.key)} title={`{${v.key}}`}
                  className="text-[11px] px-1.5 py-0.5 rounded border border-norea-border bg-white hover:border-norea-primary hover:text-norea-primary transition-colors" data-testid={`tpl-var-${v.key}`}>
                  {v.label}{v.produits?.length ? ` (${v.produits.join("/")})` : ""}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DossierPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/dossiers", { params: { search: q || undefined, limit: 8 } }).then((r) => setItems(r.data.items || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const name = (d) => [d.extraction?.prenom, d.extraction?.nom].filter(Boolean).join(" ") || d.id;
  return (
    <div className="space-y-1">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher un dossier pour l'aperçu…" className={inputCls} data-testid="tpl-preview-search" />
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid="tpl-preview-dossier">
        <option value="">— Choisir un dossier —</option>
        {items.map((d) => <option key={d.id} value={d.id}>{`${name(d)} · ${d.produit || d.extraction?.produit || ""} · ${d.extraction?.ville || ""}`}</option>)}
      </select>
    </div>
  );
}

function Preview({ tpl }) {
  const [dossierId, setDossierId] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post("/message-templates/preview", { template: tpl, dossier_id: dossierId })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Aperçu impossible"); }
    finally { setBusy(false); }
  };
  const sig = JSON.stringify(tpl);
  useEffect(() => {
    if (!dossierId) return undefined;
    const h = setTimeout(run, 600);
    return () => clearTimeout(h);
  }, [dossierId, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-2" data-testid="tpl-preview">
      <div className="flex items-end gap-2">
        <div className="flex-1"><DossierPicker value={dossierId} onChange={setDossierId} /></div>
        <button onClick={run} disabled={!dossierId || busy} className="btn-secondary text-xs" data-testid="tpl-preview-refresh">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Aperçu</button>
      </div>
      {res && (
        <div className="space-y-2 text-xs">
          <MissingVars missing={res.missing} testid="tpl-preview-missing" />
          <div className="text-norea-textMuted">Destinataire : <strong className="text-norea-text">{res.recipient?.name || res.recipient?.label || "—"}</strong></div>
          {tpl.email_body && <div className="rounded border border-norea-border bg-white p-3" data-testid="tpl-preview-email"><div className="font-medium mb-1">{res.subject}</div><pre className="whitespace-pre-wrap font-sans">{res.body}</pre></div>}
          {tpl.whatsapp && <div className="rounded border border-emerald-200 bg-emerald-50/50 p-3" data-testid="tpl-preview-whatsapp"><pre className="whitespace-pre-wrap font-sans">{res.whatsapp}</pre></div>}
        </div>
      )}
    </div>
  );
}

function CheckList({ items, value, onChange, testid }) {
  return (
    <div className="max-h-36 overflow-y-auto rounded border border-norea-border p-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid={testid}>
      {items.map((i) => (
        <label key={i.id} className="flex items-center gap-1.5 text-xs text-norea-text">
          <input type="checkbox" checked={value.includes(i.id)} onChange={(e) => onChange(e.target.checked ? [...value, i.id] : value.filter((x) => x !== i.id))} className="accent-norea-primary" data-testid={`${testid}-${i.id.replace(":", "-")}`} />
          <span className="truncate">{i.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function TemplateEditor({ template, meta, families, onClose, onSaved }) {
  const [t, setT] = useState(template);
  const [vars, setVars] = useState([]);
  const [busy, setBusy] = useState(false);
  const focus = useRef({ field: "email_body", el: null });
  useEffect(() => { api.get("/message-templates/variables").then((r) => setVars(r.data.items || [])).catch(() => {}); }, []);
  const set = (k) => (e) => setT({ ...t, [k]: e.target.value });
  const track = (field) => ({ onFocus: (e) => { focus.current = { field, el: e.target }; }, "data-field": field });
  const insert = (key) => {
    const { field, el } = focus.current;
    const cur = t[field] || "";
    const pos = el && el.selectionStart != null ? el.selectionStart : cur.length;
    const token = `{${key}}`;
    setT({ ...t, [field]: cur.slice(0, pos) + token + cur.slice(el?.selectionEnd ?? pos) });
    setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(pos + token.length, pos + token.length); } }, 0);
  };
  const fam = families.find((f) => (f.team_field || f.id) === t.role);
  const save = async () => {
    setBusy(true);
    const { id, system, created_at, updated_at, tone, ...body } = t; // eslint-disable-line no-unused-vars
    try {
      if (template.id) await api.put(`/message-templates/${template.id}`, body);
      else await api.post("/message-templates", body);
      toast.success("Modèle enregistré");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[92vh] flex flex-col border border-norea-border" data-testid="tpl-editor">
        <div className="flex items-center justify-between px-5 py-3 border-b border-norea-border">
          <h3 className="font-heading text-lg font-medium">{template.id ? "Modifier le modèle" : "Nouveau modèle"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid="tpl-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_300px_380px] gap-5 p-5 overflow-y-auto lg:overflow-hidden">
          <div className="space-y-3 lg:overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs text-norea-textMuted space-y-1"><span>Nom du modèle</span><input value={t.name} onChange={set("name")} className={inputCls} data-testid="tpl-name" /></label>
              <label className="text-xs text-norea-textMuted space-y-1"><span>Type</span>
                <select value={t.kind} disabled={t.system} onChange={set("kind")} className={inputCls} data-testid="tpl-kind">
                  {Object.entries(meta.kinds).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="text-xs text-norea-textMuted space-y-1"><span>Destinataire</span>
                <select value={t.role} disabled={t.system} onChange={(e) => setT({ ...t, role: e.target.value, partner: "" })} className={inputCls} data-testid="tpl-role">
                  <option value="">Tous les destinataires</option>
                  {Object.entries(meta.roles).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              {t.role && t.role !== "client" && (
                <label className="col-span-2 text-xs text-norea-textMuted space-y-1"><span>Propre à un partenaire (facultatif) — le modèle n'est proposé que si ce partenaire est sur le dossier</span>
                  <input list="tpl-partners" value={t.partner} onChange={set("partner")} placeholder="Tous les partenaires" className={inputCls} data-testid="tpl-partner" />
                  <datalist id="tpl-partners">{(fam?.names || []).map((n) => <option key={n} value={n} />)}</datalist>
                </label>
              )}
            </div>
            {t.kind === "envoi" && (
              <div className="text-xs text-norea-textMuted space-y-1"><span>Utilisé pour ces documents (aucun coché = tous)</span>
                <CheckList items={meta.attachments.docs.map((d) => ({ id: d.id.slice(4), label: d.label }))} value={t.docs} onChange={(v) => setT({ ...t, docs: v })} testid="tpl-docs" />
              </div>
            )}
            <div className="text-xs text-norea-textMuted space-y-1"><span>Pièces jointes par défaut</span>
              <CheckList items={[...meta.attachments.pieces, ...meta.attachments.docs.map((d) => ({ ...d, label: `${d.label} (généré)` }))]} value={t.attachments} onChange={(v) => setT({ ...t, attachments: v })} testid="tpl-attachments" />
            </div>
            <div className="p-3 rounded border border-norea-border bg-norea-bg/40 space-y-2">
              <div className="text-xs font-medium text-norea-text">Email</div>
              <input value={t.email_subject} onChange={set("email_subject")} {...track("email_subject")} placeholder="Objet" className={inputCls} data-testid="tpl-email-subject" />
              <textarea rows={9} value={t.email_body} onChange={set("email_body")} {...track("email_body")} placeholder="Corps de l'email" className={`${inputCls} font-mono text-xs`} data-testid="tpl-email-body" />
            </div>
            <div className="p-3 rounded border border-emerald-200 bg-emerald-50/40 space-y-2">
              <div className="text-xs font-medium text-norea-text">WhatsApp <span className="font-normal text-norea-textMuted">— {"{liens}"} = liens vers les pièces jointes</span></div>
              <textarea rows={6} value={t.whatsapp} onChange={set("whatsapp")} {...track("whatsapp")} placeholder="Message WhatsApp" className={`${inputCls} font-mono text-xs`} data-testid="tpl-whatsapp" />
            </div>
          </div>
          <div className="min-h-[300px] lg:min-h-0 flex flex-col">
            <div className="text-xs font-medium text-norea-text mb-1">Variables <span className="font-normal text-norea-textMuted">— cliquez pour insérer à l'endroit du curseur</span></div>
            <VariablePicker vars={vars} onPick={insert} />
          </div>
          <div className="lg:overflow-y-auto">
            <div className="text-xs font-medium text-norea-text mb-1">Aperçu sur un vrai dossier</div>
            <Preview tpl={t} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-norea-border flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Annuler</button>
          <button onClick={save} disabled={busy || !t.name.trim()} className="btn-primary text-sm" data-testid="tpl-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
